//@flow
import _ from 'lodash';
import { Color } from 'common/models/internal/Color';

export const colorList = Object.freeze({
    steel: '#67718e',
    slate: '#343947',
    'slate-grey': '#4d556a',
    'charcoal-grey': '#343947',
    dark: '#111318',
    'grey-bg': '#898989',
    black: '#111318',
    'subtle-grey': '#5f7687',

    //blues
    azure: '#28A8FB',
    'dark-sky-blue': '#0072f5',
    'dusk-blue': '#2e5b9d',

    // deprecated
    'dusk-blue-two': '#264b82',
    'cornflower-blue': '#3392ff',

    //greens
    'cool-green': '#2cc92c',
    'aqua-marine': '#56deb4',

    // deprecated
    'shamrock-green': '#2cc92c',
    'dark-mint': '#2cc92c',

    //reds & yellows
    salmon: '#f06305',
    'salmon-two': '#eb5247',
    mango: '#fdb62c',
    'sun-yellow': '#fff024',
    'star-yellow': '#f8cc2f',
    'dull-red': '#a51c12',
    'light-gold': '#ffcb63',
    'dark-gold': '#c8be2c',
    'salmon-disabled': '##bd7647',

    //light
    white: '#ffffff',
    'off-white': '#eeeeee',
    'pale-grey': '#f7f7f8',
    'white-two': '#fbfbfb',
    'white-three': '#ebebeb',
    'pale-grey-two': '#e7e9ec',
    'pale-grey-three': '#dfe1e6',
    'pale-grey-four': '#eff1f4',
    'pale-grey-five': '#f7f9fb',
    'pale-grey-six': '#EBECED',
    'pale-grey-seven': '#fafafa',
    'blue-grey-light': '#f8f8f9',
    'cloud-grey': '#bfbfbf',
    'grey-300': '#d0d3dd',
    'blue-100': '#d6e9ff',

    //special
    transparent: 'rgba(0,0,0,0)'
});

export const TourColorEnum = {
    tourColor1: '#479AF0',
    tourColor2: '#78ABA0',
    tourColor3: '#9C78AB',
    tourColor4: '#C79016',
    tourColor5: '#17B3C1',
    tourColor6: '#F37FBD',
    tourColor7: '#7A97FF',
    tourColor8: '#10828C',
    tourColor9: '#978080',
    tourColor10: '#69A9B2',
    tourColor11: '#7374BE',
    tourColor12: '#3ABBEF',
    tourColor13: '#4E892F',
    tourColor14: '#FF82AF',
    tourColor15: '#8CB6AE',
    tourColor16: '#A6AFD7',
    tourColor17: '#F19E9E',
    tourColor18: '#90549D',
    tourColor19: '#3464D6',
    tourColor20: '#BE8770',
    tourColor21: '#59C990',
    tourColor22: '#33C9D9',
    tourColor23: '#7E4444'
};

export const ZoneMapColors = Object.freeze({
    'dirty-green': '#4e892f',
    'dodger-blue': '#479af0',
    'metallic-blue': '#4c6b8a',
    'sea-green': '#78aba0',
    'dark-lilac': '#9c78ab',
    'dark-gold': '#c79016',
    turquoise: '#17b3c1',
    'rose-pink': '#f37fbd',
    'slate-blue': '#7a97ff',
    'dark-cyan': '#10828c',
    'warm-grey': '#978080',
    'medium-pink': '#ff82af',
    topaz: '#3abbef',
    'faded-blue': '#7374be',
    teal: '#69a9b2',
    'cadet-blue': '#8cb6ae',
    'cloudy-blue': '#a6afd7',
    blush: '#f19e9e',
    'dark-orchid': '#90549d',
    'warm-blue': '#3464d6',
    'indian-red': '#be8770',
    'dark-mint': '#59c990',
    aqua: '#33c9d9',
    cocoa: '#7e4444'
});
export const DistinctColors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const neoColors = {
    'blue-100': '#d6e9ff',
    'blue-200': '#add3ff',
    'blue-300': '#70b3ff',
    'blue-400': '#3392ff',
    'blue-500': '#0072f5',
    'blue-600': '#0056b8',

    'red-100': '#fbdcda',
    'red-200': '#f7bab6',
    'red-300': '#f1867e',
    'red-400': '#eb5247',
    'red-500': '#dc2518',
    'red-600': '#a51c12',
    'orange-100': '#fee7d7',
    'orange-200': '#fdceaf',
    'orange-300': '#fcaa73',
    'orange-400': '#fb8537',
    'orange-500': '#f06305',
    'orange-600': '#b44a04',

    'yellow-100': '#fff1d6',
    'yellow-200': '#ffe4ad',
    'yellow-300': '#ffcf70',
    'yellow-400': '#ffbb33',
    'yellow-500': '#f5a300',
    'yellow-600': '#b87a00',

    'purple-100': '#f5d9fd',
    'purple-200': '#ecb2fa',
    'purple-300': '#dd79f6',
    'purple-400': '#cf3ff3',
    'purple-500': '#bb0fe6',
    'purple-600': '#8c0bac',

    'violet-100': '#f3ebff',
    'violet-200': '#dac2ff',
    'violet-300': '#c299ff',
    'violet-400': '#9147ff',
    'violet-500': '#6200f5',
    'violet-600': '#4900b8',

    'green-100': '#def8de',
    'green-200': '#bcf0bc',
    'green-300': '#8ae58a',
    'green-400': '#58da58',
    'green-500': '#2cc92c',
    'green-600': '#209820',

    'grey-50': '#f9fafb',
    'grey-100': '#f3f4f6',
    'grey-200': '#e7e9ee',
    'grey-300': '#d0d3dd',
    'grey-400': '#acb2c3',
    'grey-500': '#8992a9',
    'grey-600': '#67718e',
    'grey-700': '#4d556a',
    'grey-800': '#343947',
    'grey-900': '#111318',

    'electric-blue-300': '#7fe7f0',

    white: '#ffffff'
};

const transformColors = (colorPalette: {
    [string]: string
}): {
    [colorName: string]: Color
} => {
    return Object.keys(colorPalette).reduce((colors, name) => {
        const camelCasedColor = _.camelCase(name);
        return {
            ...colors,
            [camelCasedColor]: Color.fromJS({
                colorKey: camelCasedColor,
                name,
                code: colorPalette[name]
            })
        };
    }, {});
};

// Use codegen script to generate camelCase version to make it statically typed
export const dashboardColors = transformColors(colorList);
export const zoneMapColors = transformColors(ZoneMapColors);
export const neoUIColors = transformColors(neoColors);
